import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useEffect, useState } from 'react';
import { Dialog, DialogTrigger, Modal } from 'react-aria-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InputField from '~/components/forms/InputField';
import { FormBody } from '~/components/FormUtils';
import Button from '~/components/ui/Button';
import {
  dialogActionsClassName,
  dialogCloseClassName,
  dialogContentClassName,
  dialogOverlayClassName,
  dialogPanelClassName,
} from '~/components/ui/dialogStyles';
import Icon from '~/components/ui/Icon';
import SettingsRow from '~/components/ui/SettingsRow';
import useCurrentUser from '~/modules/user/useCurrentUser';
import { useUpdateProfile } from '~/modules/user/useUpdateProfile';

type NameFormValues = {
  firstName: string;
  lastName: string;
};

type NameSectionProps = {
  isLast?: boolean;
  variant?: 'row' | 'overview';
};

export default function NameSection({ isLast, variant = 'row' }: NameSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { mutateAsync: updateProfile } = useUpdateProfile();
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || '—';

  const nameSchema = Joi.object({
    firstName: Joi.string().min(2).allow('').optional().label(t('profile.labels.firstName')),
    lastName: Joi.string().min(2).allow('').optional().label(t('profile.labels.lastName')),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
  } = useForm<NameFormValues>({
    mode: 'onTouched',
    resolver: joiResolver(nameSchema),
    values: {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await updateProfile({
      first_name: data.firstName,
      last_name: data.lastName,
    });
    setIsOpen(false);
  });

  useEffect(() => {
    if (isOpen && user) {
      reset({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
      });
    }
  }, [isOpen, user, reset]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {variant === 'overview' ? (
        <Button variant="secondary" size="sm" className="ml-auto shrink-0 gap-2">
          <Icon name="edit" className="text-lg" />
          {t('profile.editProfile')}
        </Button>
      ) : (
        <SettingsRow
          icon="badge"
          label={t('profile.nameTitle')}
          value={displayName}
          layout="split"
          isLast={isLast}
        />
      )}
      <Modal className={dialogOverlayClassName}>
        <Dialog className={dialogPanelClassName}>
          {({ close }) => (
            <div className={dialogContentClassName}>
              <button type="button" onClick={close} className={dialogCloseClassName}>
                <Icon name="close" className="text-[20px]" />
              </button>
              <div className="flex flex-col gap-4">
                <div className="mb-1.5 flex size-11 shrink-0 items-center justify-center rounded-full bg-icon-background compact:hidden">
                  <Icon name="badge" className="text-[23px] text-icon" />
                </div>
                <h2 className="text-[27px] leading-[1.2] tracking-[-0.6px] font-semibold">
                  {t('profile.editName')}
                </h2>
                <form onSubmit={onSubmit}>
                  <FormBody>
                    <InputField
                      control={control}
                      name="firstName"
                      label={t('profile.labels.firstName')}
                    />
                    <InputField
                      control={control}
                      name="lastName"
                      label={t('profile.labels.lastName')}
                    />
                  </FormBody>

                  <div className={dialogActionsClassName}>
                    <Button
                      type="submit"
                      isPending={isSubmitting}
                      isDisabled={!isValid || !isDirty}
                      className="w-full mt-2"
                    >
                      {t('profile.confirm')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
